import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Lock, Mail, ShieldCheck, ArrowLeft, Loader2, Smartphone } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BG = "https://images.unsplash.com/photo-1597773150796-e5c14ebecbf5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYmx1ZSUyMGFic3RyYWN0fGVufDB8fHx8MTc4MzQ0Mzk4NHww&ixlib=rb-4.1.0&q=85";

export default function AuthPage() {
  const navigate = useNavigate();
  const { setUser, user } = useAuth();

  const [step, setStep] = useState("login"); // login | setup | verify
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [token, setToken] = useState(""); // intermediate mfa token
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");

  const go = () => {
    const dest = sessionStorage.getItem("returnTo") || "/app";
    sessionStorage.removeItem("returnTo");
    navigate(dest.startsWith("/app") ? dest : "/app", { replace: true });
  };

  if (user && step === "login") {
    go();
  }

  const authHeader = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_setup_required) {
        setToken(data.setup_token);
        const res = await api.post("/auth/mfa/setup", {}, authHeader(data.setup_token));
        setQr(res.data.qr_code);
        setSecret(res.data.secret);
        setStep("setup");
      } else if (data.mfa_required) {
        setToken(data.mfa_token);
        setStep("verify");
      } else if (data.id) {
        setUser(data);
        go();
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/enable", { code }, authHeader(token));
      setUser(data);
      go();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { code }, authHeader(token));
      setUser(data);
      go();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRecover = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/recover", { code: recoveryCode }, authHeader(token));
      setToken(data.setup_token);
      const res = await api.post("/auth/mfa/setup", {}, authHeader(data.setup_token));
      setQr(res.data.qr_code);
      setSecret(res.data.secret);
      setCode("");
      setRecoveryCode("");
      setStep("setup");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetToLogin = () => {
    setStep("login");
    setCode("");
    setError("");
    setQr(null);
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#0B1120]/80 backdrop-blur-sm" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md rounded-2xl border border-slate-700/60 bg-[#131C31]/80 backdrop-blur-xl p-8 shadow-2xl"
      >
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8">
          <div className="w-10 h-10 rounded-lg gold-gradient flex items-center justify-center">
            <Crown className="w-6 h-6 text-black" />
          </div>
          <div className="leading-tight">
            <p className="font-display font-extrabold text-lg tracking-tight text-white">King of IT</p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-500/80">CRM</p>
          </div>
        </Link>

        {step === "login" && (
          <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
            <div>
              <h1 className="font-display font-bold text-2xl text-white">Sign in</h1>
              <p className="text-sm text-slate-400 mt-1">Access your leads pipeline.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@kingofit.ca"
                  className="w-full bg-[#0B1120] border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-[#0B1120] border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
            {error && (
              <p data-testid="auth-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="btn-primary w-full rounded-lg py-2.5 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Continue
            </button>
          </form>
        )}

        {step === "setup" && (
          <form onSubmit={handleEnable} className="space-y-5" data-testid="mfa-setup-form">
            <button type="button" onClick={resetToLogin} className="text-slate-400 hover:text-white text-sm inline-flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div>
              <div className="inline-flex items-center gap-2 text-amber-400 mb-1">
                <ShieldCheck className="w-5 h-5" />
                <h1 className="font-display font-bold text-2xl text-white">Set up MFA</h1>
              </div>
              <p className="text-sm text-slate-400">
                Scan this QR code with Google Authenticator or Microsoft Authenticator, then enter the 6-digit code.
              </p>
            </div>
            <div className="flex justify-center">
              {qr && (
                <div className="bg-white p-3 rounded-xl">
                  <img data-testid="mfa-qr-code" src={qr} alt="MFA QR code" className="w-44 h-44" />
                </div>
              )}
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 mb-1">Or enter this key manually:</p>
              <code data-testid="mfa-secret" className="font-mono text-xs text-amber-400 break-all">{secret}</code>
            </div>
            <input
              data-testid="mfa-code-input"
              inputMode="numeric"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              className="w-full bg-[#0B1120] border border-slate-700 rounded-lg px-4 py-3 text-center tracking-[0.5em] font-mono text-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            {error && <p data-testid="auth-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}
            <button data-testid="mfa-verify-button" type="submit" disabled={loading || code.length !== 6} className="btn-primary w-full rounded-lg py-2.5 flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Enable & Sign in
            </button>
          </form>
        )}

        {step === "verify" && (
          <form onSubmit={handleVerify} className="space-y-5" data-testid="mfa-verify-form">
            <button type="button" onClick={resetToLogin} className="text-slate-400 hover:text-white text-sm inline-flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div>
              <div className="inline-flex items-center gap-2 text-amber-400 mb-1">
                <Smartphone className="w-5 h-5" />
                <h1 className="font-display font-bold text-2xl text-white">Two-factor code</h1>
              </div>
              <p className="text-sm text-slate-400">Enter the 6-digit code from your authenticator app.</p>
            </div>
            <input
              data-testid="mfa-code-input"
              inputMode="numeric"
              maxLength={6}
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              className="w-full bg-[#0B1120] border border-slate-700 rounded-lg px-4 py-3 text-center tracking-[0.5em] font-mono text-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            {error && <p data-testid="auth-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}
            <button data-testid="mfa-verify-button" type="submit" disabled={loading || code.length !== 6} className="btn-primary w-full rounded-lg py-2.5 flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Verify
            </button>
            <button type="button" data-testid="mfa-lost-authenticator-link" onClick={() => { setError(""); setStep("recover"); }} className="w-full text-xs text-slate-400 hover:text-amber-400 transition-colors">
              Lost your authenticator?
            </button>
          </form>
        )}
        {step === "recover" && (
          <form onSubmit={handleRecover} className="space-y-5" data-testid="mfa-recover-form">
            <button type="button" onClick={() => { setError(""); setStep("verify"); }} className="text-slate-400 hover:text-white text-sm inline-flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div>
              <h1 className="font-display font-bold text-2xl text-white mb-1">Recover access</h1>
              <p className="text-sm text-slate-400">Enter your one-time recovery code. Your two-factor setup will be cleared and you'll scan a new QR code.</p>
            </div>
            <input data-testid="mfa-recovery-code-input" required autoFocus value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)} placeholder="Recovery code"
              className="w-full bg-[#0B1120] border border-slate-700 rounded-lg px-4 py-3 font-mono text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
            {error && <p data-testid="auth-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}
            <button data-testid="mfa-recover-button" type="submit" disabled={loading || !recoveryCode.trim()} className="btn-primary w-full rounded-lg py-2.5 flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Reset two-factor
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
